import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetUsersForManagedMeetings_users_edges_node as User,
    GetUsersForManagedMeetings as GetUsersForManagedMeetingsQuery,
    GetUsersForManagedMeetingsVariables as GetUsersForManagedMeetingsQueryVariables,
} from 'nutshell-graphql-types';

import GET_USERS_FOR_MANAGED_MEETINGS from '../queries/get-users-for-managed-meetings.graphql';

const LIMIT = 50; // Fetch in reasonable batches

export const useGetUsersForManagedMeetings = (
    skip: boolean
): {
    isLoading: boolean;
    errorMessage: string | null | undefined;
    users: User[];
} => {
    const {loading, data, error, fetchMore, networkStatus} = useQuery<
        GetUsersForManagedMeetingsQuery,
        GetUsersForManagedMeetingsQueryVariables
    >(GET_USERS_FOR_MANAGED_MEETINGS, {
        variables: {first: LIMIT},
        skip,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const users = data && data.users ? data.users : null;
    const pageInfo = users && users.pageInfo ? users.pageInfo : null;
    const hasNextPage = pageInfo ? pageInfo.hasNextPage : false;
    const endCursor = pageInfo ? pageInfo.endCursor : null;

    // Only show loading for initial fetch (networkStatus 1), not for fetchMore (networkStatus 3)
    const isInitialLoading = networkStatus === 1;

    // Automatically fetch all remaining users in the background
    React.useEffect(() => {
        if (users && hasNextPage && !loading) {
            fetchMore({
                query: GET_USERS_FOR_MANAGED_MEETINGS,
                variables: {
                    first: LIMIT,
                    after: endCursor,
                },
                updateQuery: (previousResult, {fetchMoreResult}) => {
                    if (
                        !fetchMoreResult ||
                        !fetchMoreResult.users.edges.length ||
                        !previousResult
                    ) {
                        return previousResult;
                    }

                    return {
                        users: {
                            __typename: 'UsersConnection',
                            pageInfo: fetchMoreResult.users.pageInfo,
                            edges: [...previousResult.users.edges, ...fetchMoreResult.users.edges],
                        },
                    };
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, hasNextPage, loading]);

    return {
        isLoading: isInitialLoading,
        // @ts-expect-error
        errorMessage: safelyGetGraphQLErrorMessage(error),
        users: users ? users.edges.map((edge) => edge.node) : [],
    };
};
